/**
 * Фича «Проложить трубу».
 *
 * Труба начинается кликом по точке водоснабжения (стояку, коллектору или
 * водорозетке) — из ниоткуда её не провести. Дальше клики по плану ставят
 * повороты трассы по полу, каждый участок строго горизонтальный или вертикальный.
 * Клик по другой точке подключает трубу и записывает её в проект; если точка
 * не на одной прямой с трассой, угол достраивается сам. Среда (ХВС или ГВС) и
 * материал берутся из настроек панели «Вода»; точка, не принимающая среду
 * (стояк ХВС для горячей трубы), под курсором не подсвечивается и не подключается.
 * Esc или правый клик — отменить черновик. Alt — без привязки к сетке.
 * Поворот ставится и под ванной, тумбой или шкафом: такой клик ловим на погружении
 * (`handlePointerDownCapture`), раньше, чем предмет заберёт его себе.
 *
 * Подъёмы от пола к выводам не чертятся: они следуют из высоты установки точки
 * и считаются в длине трубы автоматически.
 */
import { useEffect, useRef, useState } from "react";
import type { PointerEvent } from "react";
import type { Pt } from "@/shared/lib/geometry";
import { elbowTo, pipeEndPoint, snapOrthogonal } from "@/widgets/plan";
import { SelectionKind, Tool, useEditorStore } from "@/shared/store";
import { usePipesStore } from "@/shared/store/layers/pipesStore";
import { useProjectStore } from "@/shared/store";
import { waterMediumLabel } from "@/shared/catalog";
import { canConnectPipe } from "../logic";

interface Params {
	toWorld: (clientX: number, clientY: number) => Pt;
}

export const useDrawPipe = ({ toWorld }: Params) => {
	const tool = useEditorStore((s) => s.tool);
	const gridStep = useEditorStore((s) => s.gridStep);
	const drawResetToken = useEditorStore((s) => s.drawResetToken);
	const pipeSettings = useEditorStore((s) => s.pipeSettings);
	const setSelection = useEditorStore((s) => s.setSelection);
	const projectStore = useProjectStore();
	const pipesStore = usePipesStore();

	/** Точка, от которой ведём; null — труба не начата */
	const [from, setFrom] = useState<string | null>(null);
	/** Поставленные повороты трассы */
	const [points, setPoints] = useState<Pt[]>([]);
	/** Куда ляжет следующая точка под курсором */
	const [preview, setPreview] = useState<Pt | null>(null);
	/** Точка под курсором, к которой можно подключиться */
	const [hoverPoint, setHoverPoint] = useState<string | null>(null);
	// Дублируем в ref: обработчики читают актуальное значение между рендерами
	const fromRef = useRef<string | null>(null);
	const pointsRef = useRef<Pt[]>([]);

	const active = tool === Tool.Pipe;
	const medium = pipeSettings.medium;

	const cancel = () => {
		fromRef.current = null;
		pointsRef.current = [];
		setFrom(null);
		setPoints([]);
		setPreview(null);
		setHoverPoint(null);
	};

	// Смена инструмента или среды — черновик забывается
	useEffect(() => {
		cancel();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [drawResetToken, active, medium]);

	/** Точка на плане по актуальному чертежу. */
	const pointOf = (id: string): Pt | null => {
		const layers = projectStore.project.layers;
		return pipeEndPoint(layers.walls, layers.plumbing, id);
	};

	/** Трасса черновика: начальная точка плюс поставленные повороты. */
	const route = (): Pt[] => {
		const start = fromRef.current ? pointOf(fromRef.current) : null;
		return start ? [start, ...pointsRef.current] : [];
	};

	/** Принимает ли объект трубу текущей среды. */
	const connectable = (id: string): boolean =>
		canConnectPipe(projectStore.project.layers.plumbing, id, medium);

	/**
	 * Клик по объекту на стене инструментом трубы: начать трубу или подключить её.
	 * @returns true, если событие обработано
	 */
	const handleFixturePointerDown = (e: PointerEvent<Element>, fixtureId: string): boolean => {
		if (!active || e.button !== 0) return false;
		e.stopPropagation();
		if (!connectable(fixtureId)) return true;

		if (!fromRef.current) {
			fromRef.current = fixtureId;
			setFrom(fixtureId);
			return true;
		}
		if (fromRef.current === fixtureId) return true;

		// Подключаем: достраиваем угол, если точка не на одной прямой с трассой
		const current = route();
		const target = pointOf(fixtureId);
		if (current.length === 0 || !target) return true;
		const last = current[current.length - 1];
		const prev = current.length > 1 ? current[current.length - 2] : null;
		const extra = elbowTo(prev, last, target);
		const startId = fromRef.current;
		// Проложенная труба — свой шаг истории: Ctrl+Z убирает именно её
		const id = useProjectStore
			.getState()
			.recordEdit(() =>
				pipesStore.addPipe(
					startId,
					fixtureId,
					[...pointsRef.current, ...extra],
					medium,
					pipeSettings.material,
				),
			);
		cancel();
		if (id) setSelection({ kind: SelectionKind.Pipe, id });
		return true;
	};

	/** Клик по плану: поставить поворот трассы. @returns true, если событие обработано */
	const handlePointerDown = (e: PointerEvent<SVGSVGElement>): boolean => {
		if (!active || e.button !== 0) return false;
		if (!fromRef.current) return true; // труба начинается только с точки
		const current = route();
		if (current.length === 0) return true;
		const last = current[current.length - 1];
		const world = toWorld(e.clientX, e.clientY);
		const next = e.altKey ? world : snapOrthogonal(last, world, gridStep);
		if (Math.abs(next.x - last.x) < 1 && Math.abs(next.y - last.y) < 1) return true;
		pointsRef.current = [...pointsRef.current, next];
		setPoints(pointsRef.current);
		return true;
	};

	/** Точка под курсором, к которой можно подключить трубу; null — такой нет. */
	const endUnder = (target: EventTarget): string | null => {
		const id =
			(target as Element).closest?.('[data-role="fixture"][data-id]')?.getAttribute("data-id") ??
			null;
		if (!id || id === fromRef.current) return null;
		return connectable(id) ? id : null;
	};

	/**
	 * Клик на погружении, пока труба ведётся: всё, что не точка для подключения, — точка
	 * трассы, даже если под курсором ванна или тумба. @returns true, если событие обработано
	 */
	const handlePointerDownCapture = (e: PointerEvent<SVGSVGElement>): boolean => {
		if (!active || !fromRef.current || e.button !== 0 || e.shiftKey) return false;
		// Второй палец — щипок, его разбирает холст
		if (e.pointerType === "touch" && !e.isPrimary) return false;
		if (endUnder(e.target)) return false;
		e.stopPropagation();
		return handlePointerDown(e);
	};

	const handlePointerMove = (e: PointerEvent<SVGSVGElement>) => {
		if (!active || !fromRef.current) return;
		const over = endUnder(e.target);
		setHoverPoint(over);

		const current = route();
		if (current.length === 0) return;
		const last = current[current.length - 1];
		if (over) {
			// Над точкой показываем, что труба подключится к ней
			setPreview(pointOf(over));
			return;
		}
		const world = toWorld(e.clientX, e.clientY);
		setPreview(e.altKey ? world : snapOrthogonal(last, world, gridStep));
	};

	const draftRoute = route();
	const label = waterMediumLabel(medium);
	const hint = !active
		? null
		: !from
			? `Кликните по стояку, коллектору или водорозетке ${label} — оттуда пойдёт труба`
			: hoverPoint
				? "Клик — подключить трубу к этой точке"
				: `Клик по плану — поворот трассы (только под прямым углом), клик по точке ${label} — подключить. Esc — отменить`;

	return {
		active,
		from,
		points,
		medium,
		/** Трасса черновика от точки через повороты; пустая, если труба не начата */
		route: draftRoute,
		preview,
		canFinish: hoverPoint !== null,
		hint,
		cancel,
		handleFixturePointerDown,
		handlePointerDown,
		handlePointerDownCapture,
		handlePointerMove,
	};
};
